import React from 'react';
import { Link } from 'react-router-dom';
import { useMenu } from '../../hooks/useMenu';
import { faqs } from '../../data';
import Bredcom from '../Bredcrumb/Main';
import Offer from '../Allmenus/FirstMenu/Offers';
import Reservation from '../Allmenus/FirstMenu/Reservation';

function MenuOverview({ 
  title = "OUR MENU CATEGORIES",
  subtitle = "Explore Our Delicious Offerings",
  bannerImage,
  showOffers = true,
  showReservation = true 
}) {
  const { menu, loading, error } = useMenu();

  // Fallback static data when CMS is unavailable
  const staticCategories = React.useMemo(() => {
    const categoryMap = new Map();
    
    faqs.forEach((item, index) => {
      const categoryName = item.foodCategory;
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          id: index + 1,
          name: categoryName,
          slug: categoryName.toLowerCase().replace(/\s+/g, '-'),
          description: `Explore our delicious ${categoryName.toLowerCase()} selection`,
          items: []
        });
      }
    });

    return Array.from(categoryMap.values());
  }, []);

  const fallbackMenu = {
    categories: staticCategories,
    totalItems: faqs.length,
    totalCategories: staticCategories.length
  };

  // Helper function to map category slugs to static menu routes
  const getStaticMenuRoute = (slug) => {
    const routeMap = {
      'breakfast': '/menuone',
      'pizza': '/menutwo',
      'salads': '/menuthree',
      'salad-appetizers': '/menuthree',
      'hot-appetizers': '/menuthree',
      'cold-appetizers': '/menuthree',
      'appetizers': '/menuthree',
      'coffee-tea': '/menufour',
      'drinks': '/menufour',
      'beverages': '/menufour',
      'cocktails': '/menufour',
      'mocktails': '/menufour'
    };
    return routeMap[slug] || '/menu'; // fallback to main menu if no specific route
  };

  // Default category images using existing resource images
  const categoryImages = {
    'breakfast': '/assets/images/resource/featured-1.jpg',
    'baked-oven': '/assets/images/resource/featured-2.jpg',
    'salad-appetizers': '/assets/images/resource/featured-3.jpg',
    'hot-appetizers': '/assets/images/resource/featured-4.jpg',
    'pizza': '/assets/images/resource/featured-5.jpg',
    'pasta': '/assets/images/resource/featured-6.jpg',
    'sandwiches': '/assets/images/resource/featured-7.jpg',
    'soup': '/assets/images/resource/featured-8.jpg',
    'casserole': '/assets/images/resource/featured-9.jpg',
    'main-course': '/assets/images/resource/featured-10.jpg',
    'coffee-tea': '/assets/images/resource/drink-1.png',
    'cocktails': '/assets/images/resource/drink-2.png',
    'mocktails': '/assets/images/resource/drink-3.png',
    'juices-smoothies': '/assets/images/resource/drink-4.png',
    'cold-coffee': '/assets/images/resource/drink-5.png',
    'soft-drink': '/assets/images/resource/drink-6.png',
    'fruity-frappe': '/assets/images/resource/drink-7.png',
    'yoghurt': '/assets/images/resource/dessert-1.png',
    'milkshake': '/assets/images/resource/dessert-2.png',
    'shisha': '/assets/images/resource/featured-11.jpg'
  };

  if (loading) {
    return (
      <>
        {bannerImage && (
          <Bredcom subtitle={subtitle} title={title} Img={bannerImage} />
        )}
        <section className="menu-overview">
          <div className="auto-container">
            <div className="loading-state text-center" style={{padding: '100px 0'}}>
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p style={{marginTop: '20px', fontSize: '18px'}}>Loading menu categories...</p>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Use fallback data if CMS fails but REACT_APP_FALLBACK_TO_STATIC is true
  const shouldUseFallback = error && process.env.REACT_APP_FALLBACK_TO_STATIC === 'true';
  const currentMenu = shouldUseFallback ? fallbackMenu : menu;
  const categoriesData = currentMenu?.categories || [];

  // Only show error if no fallback data available
  if (error && !shouldUseFallback) {
    return (
      <>
        {bannerImage && (
          <Bredcom subtitle={subtitle} title={title} Img={bannerImage} />
        )}
        <section className="menu-overview">
          <div className="auto-container">
            <div className="error-state text-center" style={{padding: '100px 0'}}>
              <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
              <h3>Unable to load menu</h3>
              <p>We're having trouble loading our menu. Please try again later.</p>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      {bannerImage && (
        <Bredcom subtitle={subtitle} title={title} Img={bannerImage} />
      )}

      {/* Menu Categories Overview */}
      <section className="menu-overview">
        <div className="auto-container">
          
          {/* Section Header */}
          <div className="sec-title centered">
            <h2>Discover Our Menu</h2>
            <div className="text">
              Choose from {categoriesData.length} carefully crafted categories featuring 
              {currentMenu?.totalItems && ` over ${currentMenu.totalItems} delicious items`}
              {shouldUseFallback && (
                <small style={{display: 'block', marginTop: '10px', color: '#6c757d', fontStyle: 'italic'}}>
                  Showing static menu - live updates coming soon!
                </small>
              )}
            </div>
          </div>

          {/* Categories Grid */}
          <div className="categories-grid">
            {categoriesData.map((category) => {
              // Use different routing based on whether we're using fallback data
              const categoryLink = shouldUseFallback 
                ? getStaticMenuRoute(category.slug)
                : `/menu/${category.slug}`;
              
              return (
                <div key={category.id} className="category-card">
                  <Link to={categoryLink} className="category-link">
                  <div className="category-image">
                    <img 
                      src={categoryImages[category.slug] || '/assets/images/menu-categories/default.jpg'} 
                      alt={category.name}
                      onError={(e) => {
                        e.target.src = '/assets/images/resource/image-1.jpg'; // Fallback image
                      }}
                    />
                    <div className="category-overlay">
                      <div className="overlay-content">
                        <i className="fas fa-utensils"></i>
                        <span>View Menu</span>
                      </div>
                    </div>
                  </div>
                  <div className="category-info">
                    <h4 className="category-name">{category.name}</h4>
                    <p className="category-description">
                      {category.description || `Explore our delicious ${category.name.toLowerCase()} selection`}
                    </p>
                    <div className="category-stats">
                      <span className="item-count">
                        <i className="fas fa-list me-1"></i>
                        {category.items?.length || 0} items
                      </span>
                      {category.items && category.items.some(item => item.is_featured) && (
                        <span className="featured-badge">
                          <i className="fas fa-star me-1"></i>
                          Featured
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
                </div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="menu-cta text-center">
            <h3>Can't decide what to order?</h3>
            <p>Our friendly staff is here to help you choose the perfect meal</p>
            <div className="cta-buttons">
              <Link to="/contact" className="theme-btn btn-style-one">
                <span className="txt">Contact Us</span>
              </Link>
              <Link to="/reservation" className="theme-btn btn-style-two">
                <span className="txt">Make Reservation</span>
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* Offers Section */}
      {showOffers && <Offer />}

      {/* Reservation Section */}
      {showReservation && <Reservation />}

      <style jsx>{`
        .menu-overview {
          padding: 100px 0;
          background-color: #f8f9fa;
        }

        .sec-title.centered {
          text-align: center;
          margin-bottom: 70px;
        }

        .sec-title h2 {
          color: #2c3e50;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .sec-title .text {
          font-size: 1.1rem;
          color: #6c757d;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 30px;
          margin-bottom: 80px;
        }

        .category-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          transition: all 0.4s ease;
          position: relative;
        }

        .category-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .category-link {
          text-decoration: none;
          color: inherit;
          display: block;
        }

        .category-image {
          position: relative;
          height: 220px;
          overflow: hidden;
        }

        .category-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.4s ease;
        }

        .category-card:hover .category-image img {
          transform: scale(1.1);
        }

        .category-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, rgba(201, 48, 44, 0.8), rgba(201, 48, 44, 0.6));
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .category-card:hover .category-overlay {
          opacity: 1;
        }

        .overlay-content {
          text-align: center;
          color: white;
        }

        .overlay-content i {
          font-size: 2rem;
          margin-bottom: 0.5rem;
          display: block;
        }

        .overlay-content span {
          font-size: 1.1rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .category-info {
          padding: 25px;
        }

        .category-name {
          font-size: 1.4rem;
          font-weight: 700;
          color: #2c3e50;
          margin-bottom: 0.5rem;
        }

        .category-description {
          color: #6c757d;
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 1rem;
        }

        .category-stats {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
        }

        .item-count {
          color: #495057;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .featured-badge {
          background: linear-gradient(45deg, #ffc107, #ff8c00);
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .menu-cta {
          background: white;
          padding: 50px 30px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        .menu-cta h3 {
          color: #2c3e50;
          font-size: 1.8rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .menu-cta p {
          color: #6c757d;
          font-size: 1.1rem;
          margin-bottom: 2rem;
        }

        .cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .loading-state, .error-state {
          background: white;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .categories-grid {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .sec-title h2 {
            font-size: 2rem;
          }

          .category-image {
            height: 180px;
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-buttons .theme-btn {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 480px) {
          .menu-overview {
            padding: 60px 0;
          }

          .category-info {
            padding: 20px;
          }

          .menu-cta {
            padding: 40px 20px;
          }
        }
      `}</style>
    </>
  );
}

export default MenuOverview;